import { showSelectOptionModal } from '@lmt/unicorn-shared/app/components/common/Dialog/Dialog';
import { reasonsForPatientRemoving } from '@lmt/unicorn-shared/app/components/patients/common';
import i18next from 'i18next';
import { useModal } from '../../components/common/ui-kit/modal/useModal';
import { QueryFactory } from '@lmt/shared-types';
import { convertToErrorString } from '../../helpers/form/useErrorHandler';

function t(value: string, options?: any): string {
  return i18next.t(
    `translation:patients.remove/restore.${value}` as any,
    options,
  ) as any as string;
}

export async function confirmArchivePatient(
  modals: ReturnType<typeof useModal>,
): Promise<string | null> {
  const result = await modals.showConfirm({
    title: t('confirmations.archive.title'),
    text: t('confirmations.archive.text'),
    okButtonText: t('confirmations.archive.ok_button'),
  });
  if (!result) {
    return null;
  }
  return modals.showPrompt({
    title: t('confirmations.archive_reason.title'),
    text: t('confirmations.archive_reason.text'),
    okButtonText: t('confirmations.archive_reason.ok_button'),
    defaultValue: '',
    isMandatory: true,
  });
}

export async function confirmRestorePatient(
  modals: ReturnType<typeof useModal>,
): Promise<boolean> {
  return (
    (await modals.showConfirm({
      title: t('confirmations.restore.title'),
      text: t('confirmations.restore.text'),
      okButtonText: t('confirmations.restore.ok_button'),
    })) ?? false
  );
}

export async function confirmAnonymizePatient(
  modals: ReturnType<typeof useModal>,
  patientName: string,
): Promise<string | null> {
  const reason = await showSelectOptionModal(modals, {
    title: t('confirmations.anonymize_reason.title'),
    text: t('confirmations.anonymize_reason.text'),
    okButtonText: t('confirmations.anonymize_reason.ok_button'),
    options: reasonsForPatientRemoving.map((reason) =>
      t(`remove_reasons.${reason}`),
    ),
    showCustomOption: true,
  });
  if (!reason) return null;
  const name = await modals.showPrompt({
    title: t('confirmations.anonymize.title'),
    text: t('confirmations.anonymize.text', {
      name: patientName,
    }),
    okButtonText: t('confirmations.anonymize.ok_button'),
    defaultValue: '',
    isMandatory: true,
  });
  if (!name) return null;
  if (name.trim() === patientName) {
    return reason;
  } else {
    await modals.showAlert({
      title: t('warnings.wrong_patient_name_title'),
      text: t('warnings.wrong_patient_name_text'),
    });
    return null;
  }
}

export async function confirmMovePatient(
  modals: ReturnType<typeof useModal>,
  subtenants: string[],
): Promise<string | null> {
  const subtenant = await showSelectOptionModal(modals, {
    title: t('confirmations.move_patient.title'),
    text: t('confirmations.move_patient.text'),
    okButtonText: t('confirmations.move_patient.ok_button'),
    options: subtenants,
  });
  if (!subtenant) return null;
  const isConfirm = await modals.showConfirm({
    text: t('confirmations.move_patient_confirm.text', {
      subtenant: subtenant,
    }),
  });
  if (isConfirm) {
    return subtenant;
  }
  return null;
}

export async function confirmMovePatientWithNumberReplace(
  modals: ReturnType<typeof useModal>,
): Promise<boolean> {
  return (
    (await modals.showConfirm({
      text: t('confirmations.move_patient_with_number_replace.text'),
    })) ?? false
  );
}

export async function confirmPermanentlyDeletePatient(
  modals: ReturnType<typeof useModal>,
  patientName: string,
): Promise<boolean> {
  const name = await modals.showPrompt({
    title: t('confirmations.permanently_delete.title'),
    text: t('confirmations.permanently_delete.text', {
      name: patientName,
    }),
    okButtonText: t('confirmations.permanently_delete.ok_button'),
    defaultValue: '',
    isMandatory: true,
  });

  if (!name) {
    return false;
  }
  if (name.trim() === patientName) {
    return true;
  } else {
    await modals.showAlert({
      title: t('warnings.wrong_patient_name_title'),
      text: t('warnings.wrong_patient_name_text'),
    });
    return false;
  }
}

export async function confirmCancelFutureVisitsWhenDeceased(
  modals: ReturnType<typeof useModal>,
) {
  return (
    (await modals.showConfirm({
      title: t('confirmations.cancel_visits.title'),
      text: t('confirmations.cancel_visits.text'),
      okButtonText: t('confirmations.cancel_visits.ok_button'),
    })) ?? false
  );
}
